import React, {useMemo} from "react";
import {Stepper} from "@chakra-ui/react";
import iso6391 from "iso-639-1";
import StepSourceLanguage from "./StepSourceLanguage.tsx";
import StepTargetLanguages from "./StepTargetLanguages.tsx";
import CreateProjectModalFooter from "./CreateProjectModalFooter.tsx";
import type {ILanguageConfiguration} from "../../models/interfaces/languageConfiguration.interface.ts";
import type {ISubtitleState} from "../../reducers/createNewProjectReducer.ts";
import {
  CREATE_NEW_PROJECT_SUBTITLE_STEPS,
  LANGUAGE_DEFAULT_CONFIGURATION
} from "../../models/constants/constants.ts";
import {getLanguageDefaultConfig, getLanguageKeepSongsDefault} from "../../utils/utils.ts";
import {isLanguagesStepValid} from "./languageStepValidation.ts";
import useSystemStore from "../../store/useSystemStore.ts";
import useUserStore from "../../store/useUserStore.ts";
import StepperStep from "../StepperStep.tsx";
import type {IStepperStep} from "../../models/interfaces/steperItem.interface.ts";
import {SubtitleFinalSetup} from "./SubtitleFinalSetup.tsx";
import type {ICreditBreakdown} from "../../utils/creditsBreakdown.ts";

export type SubtitleStep = "source" | "targets";

export interface LanguagesStepProps {
  subtitlesState: ISubtitleState;
  setSourceLanguage: (payload: Record<string, ILanguageConfiguration>) => void;
  setTargetLanguages: (payload: Record<string, ILanguageConfiguration>) => void;
  markSubtitleSettingsDirty: () => void;
  toggleIsTranscribeOnly: () => void;
  toggleKeepSongs: () => void;
  setKeepSongs: (keepSongs: boolean) => void;
  toggleDubbingScript: () => void;
  initialStep: SubtitleStep;
  onBack: () => void;
  onCreate: () => void;
  isCreateDisabled: boolean;
  isCreating?: boolean;
  estimatedCredits: number;
  availableCredits: number;
  breakdown: ICreditBreakdown;
  shouldSaveTemplate: boolean;
  templateName: string;
  setShouldSaveTemplate: (shouldSave: boolean) => void;
  setTemplateName: (name: string) => void;
}

const LanguagesStep: React.FC<LanguagesStepProps> = (
  {
    subtitlesState,
    setSourceLanguage,
    setTargetLanguages,
    markSubtitleSettingsDirty,
    toggleIsTranscribeOnly,
    toggleKeepSongs,
    setKeepSongs,
    toggleDubbingScript,
    initialStep,
    onBack,
    onCreate,
    isCreateDisabled,
    isCreating = false,
    estimatedCredits,
    availableCredits,
    breakdown,
    shouldSaveTemplate,
    templateName,
    setShouldSaveTemplate,
    setTemplateName,
  }: LanguagesStepProps) => {
  const {supportedCodeLanguages} = useSystemStore();
  const {languageConfig} = useUserStore();
  const stepScrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [activeSubtitleStep, setActiveSubtitleStep] = React.useState<SubtitleStep>(initialStep);

  const supportedLanguageNames: string[] = useMemo(
    () =>
      supportedCodeLanguages
        .map((langCode: string) => iso6391.getName(langCode))
        .filter((langName: string) => langName !== ""),
    [supportedCodeLanguages]
  );

  const selectedSourceLanguage: string = Object.keys(subtitlesState.sourceLanguage)[0] || "";
  const isTranscribeOnly: boolean = Boolean(subtitlesState.isTranscribeOnly);
  const currentSubtitleStep: SubtitleStep = isTranscribeOnly ? "source" : activeSubtitleStep;
  const visibleSubtitleSteps: IStepperStep[] = isTranscribeOnly
    ? CREATE_NEW_PROJECT_SUBTITLE_STEPS.slice(0, 1)
    : CREATE_NEW_PROJECT_SUBTITLE_STEPS;
  const activeSubtitleStepIndex: number = currentSubtitleStep === "targets" ? 1 : 0;
  const hasSourceLanguage: boolean = selectedSourceLanguage !== "";
  const isFinalSubtitleStep: boolean = isTranscribeOnly || currentSubtitleStep === "targets";

  const handleSelectSourceLanguage = (
    lang: string,
    configUpdate?: Partial<ILanguageConfiguration>
  ): void => {
    if (configUpdate !== undefined) {
      markSubtitleSettingsDirty();
    } else if (!subtitlesState.keepSongsTouched) {
      // A language pick seeds Keep songs from the workspace default next to the timing seed below;
      // once the user has toggled the box, their choice outlives any later source change.
      setKeepSongs(getLanguageKeepSongsDefault(languageConfig, lang) ?? false);
    }
    setSourceLanguage({
      [lang]: {
        ...subtitlesState.sourceLanguage[lang],
        ...configUpdate || getLanguageDefaultConfig(languageConfig, lang)
      }
    });
  };

  const handleAddTargetLanguage = (lang: string): void => {
    setTargetLanguages({
      ...subtitlesState.targetLanguages,
      [lang]: getLanguageDefaultConfig(languageConfig, lang)
    });
  };

  const handleRemoveTargetLanguage = (lang: string): void => {
    const remainingTargets: Record<string, ILanguageConfiguration> = {...subtitlesState.targetLanguages};
    delete remainingTargets[lang];
    setTargetLanguages(remainingTargets);
  };

  const handleUpdateTargetConfig = (lang: string, update: Partial<ILanguageConfiguration>): void => {
    markSubtitleSettingsDirty();
    setTargetLanguages({
      ...subtitlesState.targetLanguages,
      [lang]: {
        ...subtitlesState.targetLanguages[lang],
        ...update
      }
    });
  };

  const resetStepScroll = React.useCallback((): void => {
    const scrollArea: HTMLDivElement | null = stepScrollAreaRef.current;
    if (scrollArea === null) return;

    if (typeof scrollArea.scrollTo === "function") {
      scrollArea.scrollTo({top: 0, behavior: "instant"});
    } else {
      scrollArea.scrollTop = 0;
    }
    const dialog: Element | null = scrollArea.closest('[role="dialog"]');
    if (!(dialog instanceof HTMLElement)) return;
    if (typeof dialog.scrollTo === "function") {
      dialog.scrollTo({top: 0, behavior: "instant"});
    } else {
      dialog.scrollTop = 0;
    }
  }, []);

  const goToTargetLanguages = (): void => {
    if (!hasSourceLanguage) return;
    resetStepScroll();
    setActiveSubtitleStep("targets");
  };

  const goBack = (): void => {
    if (currentSubtitleStep === "targets") {
      resetStepScroll();
      setActiveSubtitleStep("source");
      return;
    }
    onBack();
  };

  const finalSetup = isFinalSubtitleStep ? (
    <SubtitleFinalSetup
      estimatedCredits={estimatedCredits}
      availableCredits={availableCredits}
      breakdown={breakdown}
      canSaveTemplate={hasSourceLanguage}
      shouldSaveTemplate={shouldSaveTemplate}
      templateName={templateName}
      setShouldSaveTemplate={setShouldSaveTemplate}
      setTemplateName={setTemplateName}
    />
  ) : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        ref={stepScrollAreaRef}
        data-testid="languages-step-scroll-area"
        className="min-h-0 flex-1 overflow-y-auto pb-8 pr-1"
      >
        <div className="flex min-h-full">
          <Stepper
            index={activeSubtitleStepIndex}
            orientation="vertical"
          >
            {visibleSubtitleSteps.map((step: IStepperStep, index: number) => (
              <StepperStep
                key={step.title}
                step={step}
                isVertical
                isCompleted={index < activeSubtitleStepIndex}
                isActive={index === activeSubtitleStepIndex}
              />
            ))}
          </Stepper>

          <div className="ml-6 min-w-0 flex-1">
            {currentSubtitleStep === "source" && (
              <section data-testid="languages-step-source-section" className="flex min-w-0 flex-col gap-2">
                <StepSourceLanguage
                  sourceLanguages={supportedLanguageNames}
                  selectedSourceLang={selectedSourceLanguage}
                  config={subtitlesState.sourceLanguage[selectedSourceLanguage] || LANGUAGE_DEFAULT_CONFIGURATION}
                  onSelect={handleSelectSourceLanguage}
                  onClearSource={() => setSourceLanguage({})}
                  onUpdateConfig={(partial: Partial<ILanguageConfiguration>) => {
                    if (selectedSourceLanguage) handleSelectSourceLanguage(selectedSourceLanguage, partial);
                  }}
                  transcribeOnly={isTranscribeOnly}
                  toggleIsTranscribeOnly={toggleIsTranscribeOnly}
                  keepSongs={Boolean(subtitlesState.keepSongs)}
                  toggleKeepSongs={toggleKeepSongs}
                  dubbingScript={Boolean(subtitlesState.dubbingScript)}
                  toggleDubbingScript={toggleDubbingScript}
                />
              </section>
            )}

            {currentSubtitleStep === "targets" && (
              <section data-testid="languages-step-targets-section" className="flex min-w-0 flex-col gap-2">
                <StepTargetLanguages
                  availableLanguages={supportedLanguageNames}
                  selectedSourceLanguage={selectedSourceLanguage}
                  selectedTargets={subtitlesState.targetLanguages}
                  onAddLanguage={handleAddTargetLanguage}
                  onRemoveLanguage={handleRemoveTargetLanguage}
                  setTargetConfig={handleUpdateTargetConfig}
                />
              </section>
            )}
          </div>
        </div>
      </div>

      {finalSetup}

      <div className={`${isFinalSubtitleStep ? "mt-4" : "mt-6"} flex shrink-0 justify-end gap-4`}>
        {currentSubtitleStep === "source" && (
          <button
            type="button"
            className="rounded-lg bg-brandPurple-500 px-4 py-2 font-semibold text-white"
            onClick={goBack}
          >
            Back
          </button>
        )}
        <CreateProjectModalFooter
          activeStep={activeSubtitleStepIndex}
          finalStep={isTranscribeOnly ? 0 : 1}
          finalStepTitle="Create"
          canContinueToNextStep={hasSourceLanguage}
          isFinalStepDisabled={isCreateDisabled}
          isFinalStepLoading={isCreating}
          goToNext={goToTargetLanguages}
          goToPrevious={goBack}
          onFinalStepClicked={onCreate}
        />
      </div>
    </div>
  );
};

export default LanguagesStep;
